import apiClient from './apiClient';
import { ENDPOINTS, STORAGE_KEYS, ROLES } from '../constants/api';

/**
 * Auth Service
 * Handles all authentication logic: login, logout, token/user persistence.
 * The service enforces admin-only access by checking the returned role.
 */

/**
 * Login — calls the backend and validates the admin role.
 * @param {{ phone: string, password: string }} credentials
 * @returns {{ token: string, user: object }} on success
 * @throws Error with a user-facing message on failure
 */
export async function login({ phone, password }) {
  const { data: response } = await apiClient.post(ENDPOINTS.auth.login, {
    phone,
    password,
  });

  const token = response?.meta?.token;
  const user = response?.data;
  const role = user?.roleName;

  // Enforce admin-only access on the frontend as an additional guard.
  // The primary enforcement is on the backend.
  if (role !== ROLES.ADMIN) {
    throw new Error(
      `Access denied. Your role is ${role}. Only administrators can log in to this panel.`
    );
  }

  persistSession({ token, user });
  return { token, user };
}

/**
 * Persist token + user object to localStorage.
 * @param {{ token: string, user?: object }} data
 */
function persistSession(data) {
  if (data.token) {
    localStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
  }
  if (data.user) {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
  }
}

/** Clear auth state and remove persisted session. */
export function logout() {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
}

/** @returns {object|null} The stored user object, or null. */
export function getStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** @returns {boolean} Whether a valid token exists. */
export function isAuthenticated() {
  return Boolean(localStorage.getItem(STORAGE_KEYS.TOKEN));
}
